/**
 * Modern Design System & Theme Configuration
 * Unified color palette, typography, spacing, and shadows
 */

export const colors = {
  // Primary
  primary: '#0f766e',
  primaryLight: '#5eead4',
  primaryDark: '#115e59',
  primaryGlow: 'rgba(15, 118, 110, 0.16)',

  // Secondary
  secondary: '#0d9488',
  secondaryLight: '#99f6e4',
  secondaryGlow: 'rgba(20, 184, 166, 0.14)',

  // Semantic
  success: '#16a34a',
  successLight: '#86efac',
  successGlow: 'rgba(16, 163, 74, 0.12)',
  warning: '#f59e0b',
  warningLight: '#fcd34d',
  warningGlow: 'rgba(245, 158, 11, 0.12)',
  danger: '#ef4444',
  dangerLight: '#fecaca',
  dangerGlow: 'rgba(239, 68, 68, 0.12)',
  info: '#0f766e',
  infoLight: '#99f6e4',
  infoGlow: 'rgba(15, 118, 110, 0.12)',

  // Neutral
  bg: '#f0fdfa',
  bgDeep: '#ccfbf1',
  surface: '#ffffff',
  surfaceHover: '#f5fffc',
  border: '#99f6e4',
  borderLight: '#d1fae5',
  borderDark: '#2dd4bf',

  // Text
  text: '#0f172a',
  textSecondary: '#334155',
  textTertiary: '#64748b',
  textDisabled: '#64748b',
  textInverse: '#ffffff',
};

export const typography = {
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  fontFamilyMono: "'Fira Code', monospace",

  // Sizes
  h1: { fontSize: '36px', fontWeight: '800', lineHeight: '1.2', letterSpacing: '-0.02em' },
  h2: { fontSize: '30px', fontWeight: '700', lineHeight: '1.3', letterSpacing: '-0.015em' },
  h3: { fontSize: '24px', fontWeight: '700', lineHeight: '1.35', letterSpacing: '-0.01em' },
  h4: { fontSize: '20px', fontWeight: '700', lineHeight: '1.4' },
  h5: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  h6: { fontSize: '16px', fontWeight: '600', lineHeight: '1.5' },

  body1: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  body2: { fontSize: '14px', fontWeight: '400', lineHeight: '1.6' },
  bodySmall: { fontSize: '13px', fontWeight: '400', lineHeight: '1.5' },

  label: { fontSize: '13px', fontWeight: '600', lineHeight: '1.5', letterSpacing: '0.004em' },
  labelSmall: { fontSize: '12px', fontWeight: '600', lineHeight: '1.4', letterSpacing: '0.006em' },

  caption: { fontSize: '12px', fontWeight: '400', lineHeight: '1.4', color: colors.textTertiary },
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  xxxl: '48px',
};

export const radius = {
  xs: '4px',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

export const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.03)',
  md: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
  xl: '0 16px 48px rgba(0, 0, 0, 0.14), 0 8px 16px rgba(0, 0, 0, 0.08)',
  glow: {
    blue: `0 0 20px ${colors.primaryGlow}`,
    green: `0 0 20px ${colors.successGlow}`,
    purple: `0 0 20px ${colors.secondaryGlow}`,
    orange: `0 0 20px ${colors.warningGlow}`,
    red: `0 0 20px ${colors.dangerGlow}`,
  }
};

export const transitions = {
  fast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  base: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const breakpoints = {
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px',
};

// Mixed styles
export const mixins = {
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column',
  },
  glass: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: `1px solid rgba(255, 255, 255, 0.2)`,
  },
  gradientBg: {
    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`,
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const animations = {
  fadeIn: `fadeIn 0.3s ease-in-out`,
  slideUp: `slideUp 0.3s ease-out`,
  slideDown: `slideDown 0.3s ease-out`,
  pulse: `pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
};

export const zIndex = {
  hide: -1,
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modal: 400,
  tooltip: 500,
  notification: 600,
};

export default {
  colors,
  typography,
  spacing,
  radius,
  shadows,
  transitions,
  breakpoints,
  mixins,
  animations,
  zIndex,
};


